/**
 * Parameter schemas + resolution (docs/bim-plan/01-architecture.md §4).
 * ResolvedElement = instance params over type params over schema defaults, with assembly
 * layers resolved and `conditionalOn` visibility applied — computed ONCE here so 3D, 2D,
 * palette, and headless queries all see identical values.
 */
import type {
  AssemblyRecord, CategoryId, ElementId, ElementRecord, LevelId, LevelRecord,
  MaterialId, MaterialRecord, ParamValue, PhaseId, TypeId, TypeRecord,
} from './types'

export interface ParamDef {
  key: string
  label: string
  /** 'material' (P6a): a MaterialId reference stored as a plain string ('' = the
   *  category's built-in default look) — the palette renders a swatch picker.
   *  'data' (topo round): opaque structured JSON (topo points/breaklines/modifiers)
   *  — declared so resolution carries it, but never palette-rendered and never
   *  parameter-transferred (excluded like 'path2d').
   *  'typeRef' (FOOTINGS round): a TypeId reference stored as a plain string ('' =
   *  unset — the consumer's built-in defaults), constrained to `refCategory` — the
   *  palette renders a type dropdown; resolveElement materializes the referenced
   *  type's resolved params into `paramTypes` (the paramLevels pattern). */
  kind: 'length' | 'number' | 'text' | 'bool' | 'choice' | 'level' | 'path2d' | 'material' | 'data' | 'typeRef'
  /** typeRef only: the category whose types this param may reference */
  refCategory?: CategoryId
  choices?: { value: string; label: string }[]
  /**
   * STANDARD APPLIANCE SIZES (2026-10-15): a choice whose options depend on another param —
   * the palette offers `map[value of param]` (an appliance's size list by its kind) and falls
   * back to `choices` (the union, for display) when the map has no entry. The referenced
   * param is read from the element's values, then its type params.
   */
  choicesBy?: { param: string; map: Record<string, { value: string; label: string }[]> }

  min?: number
  max?: number
  default: ParamValue
  /** Computed values shown in the palette (e.g. wall area) — never editable. */
  readonly?: boolean
  /**
   * Nullable override (ROOF round r2): `null`/absent means "inherit the standard"
   * (typically the TYPE's value — the consumer resolves the fallback). The palette
   * renders an empty field with a "type default" hint, and clearing the field
   * commits null instead of 0. Only meaningful for 'number'/'length' kinds today.
   */
  optional?: boolean
  /** what an EMPTY optional field inherits from — the palette's placeholder hint
   *  (absent = 'type default'; a wall's coverage override says 'project standard') */
  optionalHint?: string
  /**
   * Parameter-transfer exclusion (r19 — eyedropper/syringe/create-similar): a
   * positional param encodes WHERE the element sits (hostOffsetFt), not WHAT it is —
   * pick-up/inject skip it. `path2d` kinds and `readonly` defs are skipped
   * automatically. WHEN ADDING A NEW INSTANCE PARAM, decide this flag: if copying the
   * value onto another element would MOVE it rather than restyle it, mark it
   * positional. See docs/MODELER.md "Parameter transfer".
   */
  positional?: boolean
  /**
   * Shown/active ONLY when another param equals the value — or ANY of the values when
   * an array is given (e.g. headRiseFt conditional on headType ∈ ['arched','segmental']).
   * The referenced param is looked up in the SAME schema first; an INSTANCE schema's
   * conditions may also reference a TYPE param (DOORS/WINDOWS round — e.g. a door's
   * `hand` conditional on the type's `operation`): mergeAndResolve passes the resolved
   * type params as fallback context. An inactive param contributes its DEFAULT to
   * resolved params (generators stay branch-safe) and is excluded from activeParamKeys
   * (palette/schedule reporting).
   * FOOTINGS round: an ARRAY of conditions is an AND — every condition must hold
   * (a wall's stemHeightFt shows only when footingEnabled AND stemMode='manual').
   */
  conditionalOn?:
    | { param: string; equals: ParamValue | ParamValue[] }
    | { param: string; equals: ParamValue | ParamValue[] }[]
  /**
   * Never rendered by the schema-driven editors (SchemaGroups/ReadOnlySchemaSummary)
   * — the param stays real data (resolved, transferred, undoable), it just has a
   * dedicated interaction instead of a form field. First use: a wall's `flipped`
   * (Matt, 2026-08-09 — a "Flip Facing" toggle implies a right and a wrong way to
   * face a wall; flipping lives on the pet palette + Spacebar instead).
   */
  hidden?: boolean
  /**
   * STAIR BOTTOM/TOP CONSTRAINTS round: a 'length' param whose stored value is plain
   * project-relative feet like any other length, but the palette (paramFields.jsx's
   * `ElevationInput`) displays/edits it in REAL-WORLD feet through the model's site
   * datum offset (datums.ts#toRealFt/toProjectFt), converting only at that UI boundary
   * — the value itself is never stored real-world (see the topo revert doctrine in
   * types.ts). Purely a rendering hint; resolution/validation treat it as 'length'.
   */
  siteElevation?: boolean
}

export interface ParamSchema { groups: { label: string; params: ParamDef[] }[] }

export function schemaParams(schema: ParamSchema): ParamDef[] {
  return schema.groups.flatMap((g) => g.params)
}

export function findParam(schema: ParamSchema, key: string): ParamDef | undefined {
  for (const g of schema.groups) for (const p of g.params) if (p.key === key) return p
  return undefined
}

/**
 * Resolve one schema's params: raw values (instance-over-type-over-default already merged
 * by the caller) → full value record + the set of ACTIVE keys per conditionalOn.
 * conditionalOn conditions are evaluated against the merged values (a condition may itself
 * reference a conditional param; conditions are evaluated against resolved-with-default
 * values, so ordering doesn't matter).
 */
export function resolveSchemaParams(
  schema: ParamSchema,
  merged: Record<string, ParamValue>,
  context?: Record<string, ParamValue>,
): { params: Record<string, ParamValue>; activeParamKeys: Set<string> } {
  const defs = schemaParams(schema)
  const params: Record<string, ParamValue> = {}
  for (const d of defs) {
    const v = merged[d.key]
    params[d.key] = v === undefined ? d.default : v
  }
  const activeParamKeys = new Set<string>()
  for (const d of defs) {
    if (!d.conditionalOn) { activeParamKeys.add(d.key); continue }
    // FOOTINGS round: an array of conditions is an AND — every one must hold
    const conds = Array.isArray(d.conditionalOn) ? d.conditionalOn : [d.conditionalOn]
    const matches = conds.every((cond) => {
      const wanted = cond.equals
      // own-schema params win; `context` (the resolved TYPE params, when resolving an
      // instance schema) covers cross-schema conditions like hand-on-operation
      const actual = cond.param in params ? params[cond.param] : context?.[cond.param]
      return Array.isArray(wanted)
        ? (wanted as ParamValue[]).some((w) => paramValueEquals(actual, w))
        : paramValueEquals(actual, wanted)
    })
    if (matches) {
      activeParamKeys.add(d.key)
    } else {
      // inactive: contributes its default, excluded from reporting
      params[d.key] = d.default
    }
  }
  return { params, activeParamKeys }
}

export function paramValueEquals(a: ParamValue | undefined, b: ParamValue | undefined): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a === 'object' || typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b)
  return false
}

/**
 * PROJECT STRUCTURAL SETTINGS (FOOTINGS round follow-up — Matt, 2026-08-09):
 * project-wide structural standards, stored ONCE in the doc's `settings` map (key
 * 'structural') — never on types. `coverageDepthFt` is the required bury depth below
 * finish grade to top-of-footing (the frost-depth requirement): a SITE condition, so
 * putting it on footing types would force the firm library to duplicate every footing
 * size per coverage depth. A wall may override it per instance (`coverageDepthFt`,
 * nullable — the roof-overhang shadow pattern); the project value is the standard.
 */
export interface StructuralSettings { coverageDepthFt: number }
export const DEFAULT_STRUCTURAL: StructuralSettings = { coverageDepthFt: 1 }

/** Normalize whatever the settings map holds into a full StructuralSettings. */
export function normalizeStructural(raw: unknown): StructuralSettings {
  const r = raw as Partial<StructuralSettings> | undefined
  return {
    coverageDepthFt: typeof r?.coverageDepthFt === 'number' && isFinite(r.coverageDepthFt) && r.coverageDepthFt >= 0
      ? r.coverageDepthFt : DEFAULT_STRUCTURAL.coverageDepthFt,
  }
}

/**
 * RAKED WINDOW HEADS round (Matt, 2026-09-22): "the rake slope should be a special
 * parameter accessible by the instance as an override to the default in the model...
 * most likely going to be raked to match a roof configuration." One project-wide
 * default slope, the `StructuralSettings` precedent exactly — stored once in the
 * doc's `settings` map (key 'windowRake'), never on a window TYPE (a raked window
 * type doesn't imply any one slope; the slope is a SITE/DESIGN fact, like the
 * structural coverage depth). `slopeRise` is rise-per-12"-of-run — the SAME unit and
 * label convention as a roof's own `slopeRise` (roof/schema.ts), so a value copied
 * straight off the roof type reads correctly here with no conversion. A window
 * instance overrides it via its own nullable `rakeSlopeRise` param (the
 * `coverageDepthFt` instance-override shadow pattern).
 */
export interface WindowRakeSettings { slopeRise: number }
export const DEFAULT_WINDOW_RAKE: WindowRakeSettings = { slopeRise: 6 }

/** Normalize whatever the settings map holds into a full WindowRakeSettings. */
export function normalizeWindowRake(raw: unknown): WindowRakeSettings {
  const r = raw as Partial<WindowRakeSettings> | undefined
  return {
    slopeRise: typeof r?.slopeRise === 'number' && isFinite(r.slopeRise) && r.slopeRise > 0
      ? r.slopeRise : DEFAULT_WINDOW_RAKE.slopeRise,
  }
}

/** A materialized `kind: 'typeRef'` reference — see ResolvedElement.paramTypes. */
export interface ResolvedTypeRef {
  record: TypeRecord
  /** the referenced type's params resolved through ITS category's typeParams schema */
  params: Record<string, ParamValue>
  /**
   * STEM FINISH LAYERS round (2026-08-17): the referenced type's OWN resolved
   * assembly (e.g. a wall's `stemTypeId` pointing at a stem type that carries
   * real core+finish layers) — the same no-model-handle rationale as
   * `params` above, so bim-geometry generators (which never get a model
   * handle) can read a referenced type's layers without a fresh lookup.
   * `null` when the referenced type has no assemblyId (absent = not yet
   * resolved, but every typeRef resolution site always sets this field).
   */
  assembly?: AssemblyRecord | null
}

/** The fully-resolved element every consumer (3D, 2D, palette, queries) works from. */
export interface ResolvedElement {
  id: ElementId
  category: CategoryId
  typeId: TypeId
  levelId: LevelId
  hostId?: ElementId
  /** phasing (docs/bim-plan/09 §7) — the raw fields, passed through unresolved (no
   *  type/default fallback makes sense for a phase reference) */
  phaseCreated?: PhaseId
  phaseDemolished?: PhaseId
  /** instance over type over defaults, inactive params at their defaults */
  params: Record<string, ParamValue>
  /** type-level values only (same precedence minus instance), for type editors/schedules */
  typeParams: Record<string, ParamValue>
  activeParamKeys: Set<string>
  activeTypeParamKeys: Set<string>
  type: TypeRecord
  assembly: AssemblyRecord | null
  level: LevelRecord | null
  /**
   * STRUCTURE round: every `kind: 'level'` param with a set value resolves to its
   * LevelRecord here (keyed by param key — e.g. a column's `topLevelId`), populated by
   * resolveElement so generators/pick configs never re-look levels up. Absent when the
   * element has no level-kind params (every pre-structure category).
   */
  paramLevels?: Record<string, LevelRecord>
  /**
   * FOOTINGS round — the paramLevels pattern for `kind: 'typeRef'` params: the
   * referenced TypeRecord plus its params resolved through its own category's
   * typeParams schema (defaults merged), keyed by param key (a wall's
   * `footingTypeId`/`stemTypeId`). Populated by resolveElement; a dangling/unset
   * reference is simply absent — consumers fall back to built-in defaults.
   */
  paramTypes?: Record<string, ResolvedTypeRef>
  /**
   * Project structural standards (FOOTINGS follow-up), materialized by resolveElement
   * for WALLS so the foundation generator reads the project's coverage depth with no
   * model handle. Absent on non-wall categories and in raw mergeAndResolve output —
   * consumers fall back to DEFAULT_STRUCTURAL.
   */
  structural?: StructuralSettings
  /**
   * ROOF BEARING WALLS (2026-10-05): per boundary edge (input order), how far the wall
   * under it reaches past the bearing line to its outside face of STUD (2026-10-12; was
   * the finish face) — materialized by resolveElement for ROOFS (roofBearingWalls.ts) so
   * the solver adds it to the edge's overhang with no model handle. Absent = no walls
   * found / non-roof.
   */
  roofBearingWallFt?: number[]
  /** FACE-OF-STUD fix (2026-10-12): the same per-edge reach to the wall's FINISHED outside
   *  face (sheathing, siding, cladding) — the `wallFt` show members butt against. Present
   *  whenever roofBearingWallFt is. */
  roofBearingWallFaceFt?: number[]
  /** BEARS ON (2026-10-13): per boundary edge, the wall it bears on (null = none found or
   *  'none') and how that was decided — what the edge dialog's "Bears on" row shows */
  roofBearingWallEdges?: (string | null)[]
  roofBearingWallModes?: ('auto' | 'picked' | 'none')[]
  /** PLAN PERF round (2026-10-06): the ids of the walls `roofBearingWallFt` was read
   *  off — the geometry worker's dirty closure re-derives the roof when one of them
   *  changes. Absent = no bearing walls found / non-roof. */
  roofBearingWallIds?: string[]
  /**
   * Project window-rake default (RAKED WINDOW HEADS round), materialized by
   * resolveElement for WINDOWS so the opening geometry reads the project's default
   * rake slope with no model handle. Absent on non-window categories and in raw
   * mergeAndResolve output — consumers fall back to DEFAULT_WINDOW_RAKE.
   */
  windowRake?: WindowRakeSettings
  /** PROJECT STYLE phase 2: which style fields this opening took from the project */
  styleFollows?: import('./openingStyle').StyleFollows
  /** PHASED SETTINGS SETS (styleSets.ts): the extra set each group resolved from (absent = the
   *  project's original values) — the Properties panel shows it */
  styleSetIds?: Partial<Record<import('./styleSets').StyleSetGroup, string>>
  /** PROJECT STYLE phase 2: the frame's colors (hex) — exterior side, interior side; null = the
   *  finish's own color. Equal on a one-color system. */
  openingColors?: { exterior: string | null; interior: string | null; leaf?: string }
  /**
   * WALL FRAMING round: the assembly's structural-core-layer MaterialRecord,
   * materialized by resolveElement for any element with an assembly (the paramLevels
   * pattern applied to the core material) — the wood-core signal for the framing
   * derivation (wallFraming.ts#isWoodCoreWall). Absent in raw mergeAndResolve output
   * and when the layer's material id dangles — consumers treat absent as non-wood.
   */
  coreMaterial?: MaterialRecord
  /**
   * BARREL-TILE ROOFING (docs/bim-plan/12): the seeded tile-material ids, materialized
   * by resolveElement for ROOFS whose type declares a roofingProfile — resolved by
   * NAME (the firm library's adopt-by-name identity; kernel/roofTiles.ts) so the tile
   * generator reads real material ids with no model handle (the coreMaterial
   * rationale). Absent on every other category and when the profile is ''.
   */
  roofTileMaterials?: { blends: MaterialId[]; mortar?: MaterialId; flashing?: MaterialId }
  /** PAINTED ROOFING: the roofing spec of every finish painted on this roof's planes,
   *  by material id (materialized so the generator needs no model handle) */
  roofingSpecs?: Record<string, import('./types').RoofingSpec>
  /** COLORS (2026-10-05): the color a WALL's finish materials show, by material id → '#hex'
   *  (brushed face color, else the project standard) — absent = the materials' own colors */
  matTints?: Record<string, string>
  /** WALL END FINISHES (2026-10-06): the finish wrapping each free end of a wall — material
   *  + the layer's built thickness (finish + its setting bed) */
  endFinishes?: Partial<Record<'start' | 'end', { materialId: string; thicknessFt: number; spec: import('./types').CladdingSpec }>>
  /** COLORS: a ROOF's per-plane color by boundary node index ('#hex' | null) */
  planeColors?: (string | null)[]
  /** COLORS: a post's / beam's / cabinet's whole-member color ('#hex') */
  memberColor?: string
  /** DECORATIVE BOX BEAMS (2026-10-08): a ceiling's box beam boards (finish, tinted by its color)
   *  and the nailer inside them — absent when the ceiling has none */
  boxBeamMats?: { board: string; boardBase: string; nailer: string }
  /** COLORS (2026-10-06): a fence's frame + infill colors ('#hex') */
  fenceColors?: { frame?: string; infill?: string }
  /** COLORS: a roof's show-member colors by kind ('#hex') */
  roofMemberColors?: Partial<Record<'tails' | 'outlookers' | 'barge', string>>
  /** GUTTERS (2026-10-07): the gutter + downspout material id the 3D shows (tinted by Metals) */
  gutterMat?: string
  /** TRIM COLORS (2026-10-07): an opening's brushed trim colors (tokens) */
  trimTints?: { exterior?: string; interior?: string }
  /** METALS ROLE (2026-10-07): a downspout's own material, tinted by the Metals color */
  downspoutMat?: string
  /** BARE METALS (2026-10-07): a railing's metal parts' material when the Metals role is a bare metal */
  metalMat?: string
  /** FASCIA + SOFFIT COLOR (2026-10-07): the color (hex) the fascia and the eave underside show —
   *  brushed, else the Trim role — and the finish each is judged against */
  eaveColors?: { fascia: string | null; soffit: string | null; fasciaBase: string; soffitBase: string }
  /** PAINTED WALL FINISHES: the finish painted on each face of a wall (already a
   *  layer of `assembly`) — the 3D generator models the detail from these */
  claddingSpecs?: Partial<Record<'exterior' | 'interior', { materialId: string; spec: import('./types').CladdingSpec }>>
  /** DEMOLISH LAYERS round 2 (wallScopeAfter.ts): the wall once its scope phase arrives — removed
   *  layers out, new finishes on. Present only on a wall whose scope removes or adds a layer. */
  scopeAfter?: import('./wallScopeAfter').WallScopeAfter
  /** SPLIT-BAND THICKNESS: per face, each band's thickness change against the face
   *  (wallFaceSplits.ts#wallBandSteps) — present only when some band steps */
  faceBandSteps?: Partial<Record<'exterior' | 'interior', import('./wallFaceSplits').FaceBandStep[]>>
  /** the cladding spec of every finish painted on a face BAND, by material id */
  bandCladdingSpecs?: Record<string, import('./types').CladdingSpec>
  /** WALL BANDS: the wall's bands with their material resolved (`mat`, null = none
   *  found — the generator falls back to the face's own finish) */
  faceSweeps?: (import('./types').FaceSweepEntry & { mat: string | null; brickUnit?: string | null })[]
  /** BRICK MODULES (2026-10-04): the brick size (masonry.ts unit key) of a wall's exterior
   *  finish, and of a door/window's picked trim / sill materials — so a rowlock or soldier
   *  course is as wide as the bricks it's made of */
  exteriorFinishBrick?: string | null
  /** WALL COPINGS: the coping's material ids, resolved (`main` = metal / stone / brick,
   *  `tile` = the clay creasing tiles) + the brick size it's laid in */
  copingMats?: { main: string; tile: string; brick: string | null }
  trimBrick?: { trim: string | null; sill: string | null }
  /** WALL NICHE (2026-10-07): the niche's built shape against its host wall (niche.ts) */
  nicheSpec?: import('./niche').NicheSpec
  /** AWNINGS (2026-10-08): the awning's resolved layout (awning.ts) and its finishes */
  awningSpec?: import('./awning').AwningSpec
  /** CUPOLAS (2026-10-09): the cupola's finishes (tinted ids), resolved — the generator has no model */
  cupolaMats?: { body: string; clad: string; metal: string; roofing: string; roofingSpec: import('./types').RoofingSpec | null; roofingHex: string | null; glass: string; shaft: string; framing: string }
  awningMats?: { frame: string; metal: string; roofing: string; roofingSpec: import('./types').RoofingSpec | null; roofingHex: string | null; fabricA: string; fabricB: string; soffit: string; gutter?: string }
  /** SHUTTERS: a window's shutter leaf + hardware material ids, resolved */
  shutterMats?: { leaf: string; hardware: string }
  /** PAINTED FLOOR/CEILING FINISHES: thickness of every finish painted on this
   *  floor/ceiling's areas, by material id (the geometry needs no model handle) */
  slabFinishThicknessFt?: Record<string, number>
  /** FINISH COLORS (2026-10-07): each painted floor/ceiling area's color token by area id
   *  (a '#hex' or a bare metal's 'metal:<key>') — brushed, else the finish's project standard */
  finishAreaTints?: Record<string, string>
  /** DECK PLANKS: the plank recipe of every painted finish that has one, by material id */
  slabFinishPlank?: Record<string, import('./types').FloorPlankSpec>
  /** PROJECT FINISH DEFAULTS (finishDefaults.ts): the placed rooms on a floor's level with what
   *  their floor defaults to (material id, or null = nothing), plus the level's room separators —
   *  slabFinish.ts lays each default inside the room's region. Absent = no defaults apply. */
  finishDefaultRooms?: { seedPt: [number, number]; materialId: string | null; wet?: boolean }[]
  finishDefaultSeparators?: [number, number][][]
  /** FINISH TRANSITIONS: where the defaults' changes sit in doorways + the standard threshold
   *  at a wet room's doors (only when the wet floor differs from the flooring throughout) */
  /** ROOM FINISHES (roomFinishes.ts): on a FLOOR, every room on its level with wall zones, each
   *  zone resolved (z from the floor, material, thickness, tint); geometry builds them once per
   *  level (bim-geometry roomFinishes.ts) */
  roomWallFinishes?: {
    rooms: { seedPt: [number, number]; zones: { z0Ft: number; z1Ft: number | null; materialId: string; thicknessFt: number; tint: string | null }[] }[]
    separators: [number, number][][]
  }
  finishDefaultDoor?: import('./types').DoorTransition
  finishDefaultThreshold?: import('./types').ThresholdSpec | null
  /** FINISH LAYOUT: the floor finishes laid as tiles / planks, by material */
  slabFinishLayout?: Record<string, import('./types').FinishLayoutSpec>
  /** STAIR TREAD FINISH: the resolved tread finish (material must be a floor finish) */
  stairFinish?: { materialId: string; thicknessFt: number; plank?: import('./types').FloorPlankSpec }
  /** assembly layer thickness sum (0 when no assembly) — the wall's plan thickness */
  assemblyThicknessFt: number
}

export function assemblyThickness(assembly: AssemblyRecord | null | undefined): number {
  if (!assembly) return 0
  return assembly.layers.reduce((s, l) => s + (l.thicknessFt || 0), 0)
}

/** Pure merge used by resolveElement — exported for tests. */
export function mergeAndResolve(
  el: ElementRecord,
  type: TypeRecord,
  assembly: AssemblyRecord | null,
  level: LevelRecord | null,
  instanceSchema: ParamSchema,
  typeSchema: ParamSchema,
): ResolvedElement {
  const typeMerged: Record<string, ParamValue> = { ...type.params }
  const typeResolved = resolveSchemaParams(typeSchema, typeMerged)
  const instMerged: Record<string, ParamValue> = { ...el.params }
  const instResolved = resolveSchemaParams(instanceSchema, instMerged, typeResolved.params)
  return {
    id: el.id,
    category: el.category,
    typeId: el.typeId,
    levelId: el.levelId,
    hostId: el.hostId,
    phaseCreated: el.phaseCreated,
    phaseDemolished: el.phaseDemolished,
    // MODIFY PHASE-2 (Pin) fix: `pinned` is deliberately absent from every category's
    // param SCHEMA (elements.ts#elementPinned — so it never eyedropper-transfers and
    // duplicateElement can strip it), but that also meant resolveSchemaParams silently
    // dropped it here, same as any other undeclared key. Every UI consumer reads pin
    // state off `resolveElement(...).params.pinned` (HandleLayer's grip-hiding + pet
    // palette, the Pin/Unpin tool's toggle, selection-highlight color) — with it
    // missing they always saw `undefined`, so a pinned element showed full grips and
    // the Pin tool just re-pinned it every click instead of ever offering Unpin.
    // Passed through raw, outside the schema-driven merge, same as phaseCreated/
    // phaseDemolished above.
    params: {
      ...typeResolved.params, ...instResolved.params, ...(el.params.pinned === true ? { pinned: true } : {}),
      // MULLED WINDOWS (2026-10-08): the unit membership is schema-free instance state, so
      // it rides through here like `pinned` — tags, schedule, dims and the pet palette all
      // read it off the resolved element (kernel mullGroups.ts)
      ...(typeof el.params.mullGroupId === 'string' && el.params.mullGroupId
        ? { mullGroupId: el.params.mullGroupId, mullIndex: el.params.mullIndex ?? 0 } : {}),
      // SPEC LINKS (specLinks.ts): the AlderSPEC schedule row this element is — schema-free,
      // so it rides through here like `pinned`
      ...(typeof el.params.specItemId === 'string' && el.params.specItemId ? { specItemId: el.params.specItemId } : {}),
    },
    typeParams: typeResolved.params,
    activeParamKeys: instResolved.activeParamKeys,
    activeTypeParamKeys: typeResolved.activeParamKeys,
    type,
    assembly,
    level,
    assemblyThicknessFt: assemblyThickness(assembly),
  }
}
